import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Stack,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '../../components/Layout/MainLayout';
import { TimeSelect } from '../../components/UI/TimeSelect';
import { useAuth } from '../../hooks/useAuth';
import { eventService } from '../../services/eventService';
import {
  EventCategory,
  IndustryType,
  CompanyScaleType,
  LocationType,
  CATEGORY_LABELS,
  INDUSTRY_LABELS,
  COMPANY_SCALE_LABELS,
  LOCATION_TYPE_LABELS,
} from '../../types/event';

interface FormState {
  title:                string;
  description:          string;
  category:             EventCategory | '';
  event_date:           string;
  start_time:           string;
  end_time:             string;
  location:             string;
  location_type:        LocationType;
  capacity:             string;
  application_deadline: string;
  company_name:         string;
  industry:             IndustryType | '';
  company_scale:        CompanyScaleType | '';
  tag_ids:              number[];
  meeting_url:          string;
}

const INITIAL_FORM: FormState = {
  title:                '',
  description:          '',
  category:             '',
  event_date:           '',
  start_time:           '',
  end_time:             '',
  location:             '',
  location_type:        'offline',
  capacity:             '',
  application_deadline: '',
  company_name:         '',
  industry:             '',
  company_scale:        '',
  tag_ids:              [],
  meeting_url:          '',
};

const pad = (n: number) => String(n).padStart(2, '0');
const getTodayStr = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const AdminEventFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { user } = useAuth();
  const isCompanyEditor = ['company_admin', 'hr'].includes(user?.userRole ?? '');

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [savedForm, setSavedForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState<{ id: number; slug: string; label: string }[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const searchCompanies = useCallback(async (q: string) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setCompanyLoading(true);
    try {
      const res = await fetch(`/api/companies/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        signal: abortControllerRef.current.signal,
      });
      if (!res.ok) return;
      const json = await res.json();
      setCompanyOptions(json.data ?? []);
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCompanyInputChange = useCallback((_: React.SyntheticEvent, value: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => searchCompanies(value), 300);
  }, [searchCompanies]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/events/tags', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        const tags = json.data ?? [];
        setAvailableTags(
          tags.map((t: { id: number; name: string; slug: string }) => ({
            id:    t.id,
            slug:  t.slug,
            label: t.name,
          }))
        );
      } catch {
        // タグ取得失敗は無視
      }
    })();
  }, []);

  // ブラウザ「戻る」をブロック（popstate で検知し、pushState で遷移を差し戻す）
  useEffect(() => {
    if (!isDirty || isLoading) return;
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setCancelDialogOpen(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, isLoading]);

  // リロード・タブ閉じをブロック
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const event = await eventService.getEventById(parseInt(id, 10));
        const loaded: FormState = {
          title:                event.title,
          description:          event.description ?? '',
          category:             event.category,
          event_date:           event.event_date,
          start_time:           event.start_time.slice(0, 5),
          end_time:             event.end_time.slice(0, 5),
          location:             event.location,
          location_type:        event.location_type,
          capacity:             String(event.capacity),
          application_deadline: event.application_deadline ?? '',
          company_name:         event.company_name,
          industry:             event.industry ?? '',
          company_scale:        event.company_scale ?? '',
          tag_ids:              (event.tags ?? []).map((t) => t.id),
          meeting_url:          event.meeting_url ?? '',
        };
        setForm(loaded);
        setSavedForm(loaded);
      } catch {
        setSnackbar({ open: true, message: 'イベント情報の取得に失敗しました', severity: 'error' });
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isEdit, id]);

  const handleChange = (key: keyof FormState) => (e: any) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'location_type') {
        next.location = '';
        if (value !== 'online') next.meeting_url = '';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined, ...(key === 'location_type' ? { location: undefined, meeting_url: undefined } : {}) }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim())        errs.title        = 'イベント名は必須です';
    if (!form.category)            errs.category     = 'イベント種別は必須です';
    if (!form.event_date) {
      errs.event_date = '開催日は必須です';
    } else if (!isEdit && form.event_date < getTodayStr()) {
      errs.event_date = '開催日は本日以降を指定してください';
    }
    if (!form.start_time)          errs.start_time   = '開始時間は必須です';
    if (!form.end_time)            errs.end_time     = '終了時間は必須です';
    if (form.start_time >= form.end_time && form.start_time && form.end_time)
      errs.end_time = '終了時間は開始時間より後にしてください';
    if (form.location_type !== 'online' && !form.location.trim()) errs.location = '場所は必須です';
    if (form.location_type === 'online' && form.meeting_url.trim()) {
      try {
        const u = new URL(form.meeting_url.trim());
        if (u.protocol !== 'http:' && u.protocol !== 'https:') {
          errs.meeting_url = '会議URLはhttp/httpsで始まる正しいURL形式で入力してください';
        } else if (form.meeting_url.trim().length > 2048) {
          errs.meeting_url = '会議URLは2048文字以内で入力してください';
        }
      } catch {
        errs.meeting_url = '会議URLはhttp/httpsで始まる正しいURL形式で入力してください';
      }
    }
    if (!form.capacity)            errs.capacity     = '定員は必須です';
    else if (!Number.isInteger(Number(form.capacity)))
      errs.capacity = '定員は整数で入力してください';
    else if (Number(form.capacity) < 1 || Number(form.capacity) > 500)
      errs.capacity = '定員は1〜500で入力してください';
    if (!isCompanyEditor && !form.company_name.trim()) errs.company_name = '企業名は必須です';
    if (!form.application_deadline) {
      errs.application_deadline = '申込締切日は必須です';
    } else if (!isEdit && form.application_deadline < getTodayStr()) {
      errs.application_deadline = '申込締切は本日以降を指定してください';
    } else if (form.event_date && form.application_deadline > form.event_date) {
      errs.application_deadline = '申込締切は開催日以前に設定してください';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsLoading(true);
    const isOnline = form.location_type === 'online';
    const body: any = {
      title:         form.title,
      category:      form.category,
      event_date:    form.event_date,
      start_time:    form.start_time,
      end_time:      form.end_time,
      location:      isOnline ? '' : form.location,
      location_type: form.location_type,
      capacity:      Number(form.capacity),
      // company_admin / hr は会社名をサーバー側で自動セットするため送信しない
      ...(isCompanyEditor ? {} : { company_name: form.company_name }),
    };
    if (form.description)          body.description          = form.description;
    if (form.industry)             body.industry             = form.industry;
    if (form.company_scale)        body.company_scale        = form.company_scale;
    if (form.application_deadline) body.application_deadline = form.application_deadline;
    body.tag_ids = form.tag_ids;
    body.meeting_url = isOnline && form.meeting_url.trim() ? form.meeting_url.trim() : null;

    try {
      if (isEdit && id) {
        await eventService.updateEvent(parseInt(id, 10), body);
        navigate('/events', { state: { successMessage: 'イベントを更新しました' } });
      } else {
        await eventService.createEvent(body);
        navigate('/events', { state: { successMessage: 'イベントを作成しました' } });
      }
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === 'CAPACITY_BELOW_PARTICIPANTS') {
        const n = err?.response?.data?.error?.registered_count;
        setSnackbar({
          open: true,
          message: `現在${n}名が申込済みのため、${n}名未満に変更できません`,
          severity: 'error',
        });
      } else {
        const serverMessage = err?.response?.data?.error?.message;
        setSnackbar({
          open: true,
          message: serverMessage
            ? serverMessage
            : isEdit
              ? 'イベントの更新に失敗しました。もう一度お試しください'
              : 'イベントの作成に失敗しました。もう一度お試しください',
          severity: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <MainLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {isEdit ? 'イベント編集' : 'イベント作成'}
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
          <Stack spacing={3}>
            {/* イベント名 */}
            <TextField
              label="イベント名"
              value={form.title}
              onChange={handleChange('title')}
              error={!!errors.title}
              helperText={errors.title}
              required
              fullWidth
              inputProps={{ maxLength: 100 }}
            />

            {/* 企業名: admin はインクリメンタルサーチ、company_admin / hr はサーバー側で自動セット */}
            {!isCompanyEditor && (
              <Autocomplete
                options={companyOptions}
                loading={companyLoading}
                value={form.company_name || null}
                onChange={(_, value) => setForm((prev) => ({ ...prev, company_name: value ?? '' }))}
                onInputChange={handleCompanyInputChange}
                onOpen={() => searchCompanies('')}
                freeSolo={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="企業名"
                    required
                    error={!!errors.company_name}
                    helperText={errors.company_name}
                    inputProps={{ ...params.inputProps, maxLength: 100 }}
                  />
                )}
              />
            )}

            {/* イベント種別 */}
            <FormControl required error={!!errors.category} fullWidth>
              <InputLabel>イベント種別</InputLabel>
              <Select value={form.category} label="イベント種別" onChange={handleChange('category')}>
                {(Object.keys(CATEGORY_LABELS) as EventCategory[]).map((k) => (
                  <MenuItem key={k} value={k}>{CATEGORY_LABELS[k]}</MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography variant="caption" color="error" ml={1.5}>{errors.category}</Typography>
              )}
            </FormControl>

            {/* 開催日 */}
            <TextField
              label="開催日"
              type="date"
              value={form.event_date}
              onChange={handleChange('event_date')}
              error={!!errors.event_date}
              helperText={errors.event_date}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getTodayStr() }}
            />

            {/* 開始時間 */}
            <TimeSelect
              label="開始時間"
              value={form.start_time}
              onChange={(v) => {
                setForm((prev) => ({ ...prev, start_time: v }));
                setErrors((prev) => ({ ...prev, start_time: undefined }));
              }}
              required
              error={!!errors.start_time}
              helperText={errors.start_time}
            />

            {/* 終了時間 */}
            <TimeSelect
              label="終了時間"
              value={form.end_time}
              onChange={(v) => {
                setForm((prev) => ({ ...prev, end_time: v }));
                setErrors((prev) => ({ ...prev, end_time: undefined }));
              }}
              required
              error={!!errors.end_time}
              helperText={errors.end_time}
              minTime={form.start_time || undefined}
            />

            {/* 開催形式 */}
            <FormControl>
              <FormLabel>開催形式</FormLabel>
              <RadioGroup row value={form.location_type} onChange={handleChange('location_type')}>
                <FormControlLabel value="online"  control={<Radio />} label={LOCATION_TYPE_LABELS['online']} />
                <FormControlLabel value="offline" control={<Radio />} label={LOCATION_TYPE_LABELS['offline']} />
              </RadioGroup>
            </FormControl>

            {/* 場所（オフライン・ハイブリッドのみ表示） */}
            {form.location_type !== 'online' && (
              <TextField
                label="場所"
                value={form.location}
                onChange={handleChange('location')}
                error={!!errors.location}
                helperText={errors.location}
                required
                fullWidth
                inputProps={{ maxLength: 255 }}
              />
            )}

            {/* 会議URL（オンラインのみ表示） */}
            {form.location_type === 'online' && (
              <TextField
                label="会議URL（Zoom / Google Meet 等）"
                placeholder="https://zoom.us/j/..."
                value={form.meeting_url}
                onChange={handleChange('meeting_url')}
                error={!!errors.meeting_url}
                helperText={errors.meeting_url ?? 'オンラインイベントの参加URLを入力してください（任意）'}
                fullWidth
                inputProps={{ maxLength: 2048 }}
              />
            )}

            {/* 定員 */}
            <Box>
              <Typography
                component="label"
                sx={{ fontSize: '0.75rem', color: errors.capacity ? '#d32f2f' : 'text.secondary', display: 'block', mb: 0.75 }}
              >
                定員
                <Box component="span" sx={{ color: 'text.secondary', ml: 0.25 }}>*</Box>
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {[5, 10, 20, 30, 50, 100].map((n) => (
                  <Button
                    key={n}
                    size="small"
                    variant={form.capacity === String(n) ? 'contained' : 'outlined'}
                    color={errors.capacity ? 'error' : 'primary'}
                    onClick={() => handleChange('capacity')({ target: { value: String(n) } })}
                    sx={{ minWidth: 52 }}
                  >
                    {n}
                  </Button>
                ))}
              </Stack>
              <TextField
                label="または直接入力"
                type="number"
                value={form.capacity}
                onChange={handleChange('capacity')}
                error={!!errors.capacity}
                helperText={errors.capacity || '1〜500の範囲で入力してください'}
                fullWidth
                inputProps={{ min: 1, max: 500, inputMode: 'numeric', pattern: '[0-9]*' }}
                size="small"
              />
            </Box>

            {/* 申込締切日 */}
            <TextField
              label="申込締切日"
              type="date"
              value={form.application_deadline}
              onChange={handleChange('application_deadline')}
              error={!!errors.application_deadline}
              helperText={errors.application_deadline}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getTodayStr(), max: form.event_date || undefined }}
            />

            {/* 対象タグ */}
            <FormControl component="fieldset">
              <FormLabel component="legend">対象タグ（複数選択可）</FormLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                {availableTags.map((tag) => (
                  <FormControlLabel
                    key={tag.id}
                    control={
                      <Checkbox
                        checked={form.tag_ids.includes(tag.id)}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            tag_ids: e.target.checked
                              ? [...prev.tag_ids, tag.id]
                              : prev.tag_ids.filter((id) => id !== tag.id),
                          }));
                        }}
                      />
                    }
                    label={tag.label}
                  />
                ))}
              </Box>
            </FormControl>

            {/* 業種 */}
            <FormControl fullWidth>
              <InputLabel>業種</InputLabel>
              <Select value={form.industry} label="業種" onChange={handleChange('industry')}>
                <MenuItem value="">未設定</MenuItem>
                {(Object.keys(INDUSTRY_LABELS) as IndustryType[]).map((k) => (
                  <MenuItem key={k} value={k}>{INDUSTRY_LABELS[k]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 企業規模 */}
            <FormControl fullWidth>
              <InputLabel>企業規模</InputLabel>
              <Select value={form.company_scale} label="企業規模" onChange={handleChange('company_scale')}>
                <MenuItem value="">未設定</MenuItem>
                {(Object.keys(COMPANY_SCALE_LABELS) as CompanyScaleType[]).map((k) => (
                  <MenuItem key={k} value={k}>{COMPANY_SCALE_LABELS[k]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 説明 */}
            <TextField
              label="説明"
              value={form.description}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={4}
              inputProps={{ maxLength: 300 }}
              helperText={`${form.description.length}/300`}
            />
          </Stack>
        </Paper>

        {/* アクションボタン */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <Button
            variant="outlined"
            onClick={() => isDirty ? setCancelDialogOpen(true) : navigate('/events')}
            disabled={isLoading}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isLoading ? '保存中...' : '保存する'}
          </Button>
        </Stack>
      </Container>

      {/* キャンセル・離脱確認ダイアログ */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>編集内容を破棄しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">編集内容が破棄されますが、よろしいですか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>いいえ</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setCancelDialogOpen(false);
              navigate('/events');
            }}
          >
            はい
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 5000 : 3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default AdminEventFormPage;
